import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { restaurants } from '../../../mocks/foodDrink';
import { useSEO, generateRestaurantSchema, generateBreadcrumbSchema } from '../../../utils/seo';
import FavoriteButton from '../../../components/FavoriteButton';
import AuthModal from '../../../components/AuthModal';

const menuItems: Record<number, { category: string; items: { name: string; description: string; price: string }[] }[]> = {
  1: [
    { category: 'Starters', items: [
      { name: 'Beer Cheese Dip', description: 'House-made with our signature ale, served with pretzel bites', price: '$12' },
      { name: 'Crispy Brussels Sprouts', description: 'Flash fried, honey glaze, bacon crumbles', price: '$11' },
      { name: 'Loaded Nachos', description: 'House chips, beer cheese, jalapeños, pico de gallo', price: '$14' }
    ]},
    { category: 'Mains', items: [
      { name: 'Brewmaster Burger', description: '8oz Angus beef, aged cheddar, beer-braised onions, brioche bun', price: '$18' },
      { name: 'Fish & Chips', description: 'Beer-battered cod, hand-cut fries, house tartar', price: '$19' },
      { name: 'BBQ Pulled Pork Sandwich', description: 'Slow-smoked pork, tangy slaw, pickles', price: '$16' }
    ]},
    { category: 'House Beers', items: [
      { name: 'Monk\'s Golden Ale', description: 'Light, crisp, citrus notes', price: '$7' },
      { name: 'Abbey IPA', description: 'Bold hops, tropical fruit, pine finish', price: '$8' },
      { name: 'Dark Confession Stout', description: 'Rich chocolate, coffee, smooth finish', price: '$8' }
    ]}
  ],
  2: [
    { category: 'Small Plates', items: [
      { name: 'Burrata & Heirloom Tomatoes', description: 'Fresh burrata, seasonal tomatoes, basil oil, balsamic reduction', price: '$18' },
      { name: 'Seared Scallops', description: 'Pan-seared diver scallops, cauliflower purée, brown butter', price: '$22' },
      { name: 'Duck Confit Crostini', description: 'Shredded duck, fig jam, arugula, goat cheese', price: '$16' }
    ]},
    { category: 'Entrées', items: [
      { name: 'Pan-Roasted Salmon', description: 'Wild-caught salmon, lemon beurre blanc, seasonal vegetables', price: '$34' },
      { name: 'Filet Mignon', description: '8oz center-cut, truffle mashed potatoes, red wine reduction', price: '$48' },
      { name: 'Roasted Chicken', description: 'Free-range half chicken, herb jus, roasted root vegetables', price: '$28' }
    ]},
    { category: 'Desserts', items: [
      { name: 'Crème Brûlée', description: 'Classic vanilla bean, caramelized sugar', price: '$12' },
      { name: 'Chocolate Lava Cake', description: 'Warm chocolate center, vanilla ice cream', price: '$14' }
    ]}
  ],
  3: [
    { category: 'First Course', items: [
      { name: 'Seasonal Soup', description: 'Chef\'s daily creation with local ingredients', price: '$14' },
      { name: 'Farm Salad', description: 'Mixed greens, shaved vegetables, champagne vinaigrette', price: '$16' },
      { name: 'Tuna Tartare', description: 'Sushi-grade ahi, avocado, sesame, wonton crisps', price: '$19' }
    ]},
    { category: 'Main Course', items: [
      { name: 'Braised Short Rib', description: 'Red wine braised, creamy polenta, gremolata', price: '$42' },
      { name: 'Halibut', description: 'Pan-seared, spring pea risotto, lemon butter', price: '$44' },
      { name: 'Rack of Lamb', description: 'Herb-crusted, mint pesto, roasted fingerlings', price: '$46' }
    ]}
  ]
};

export default function RestaurantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const restaurantId = parseInt(id || '1');
  const restaurant = restaurants.find(r => r.id === restaurantId);
  const menu = menuItems[restaurantId];
  const [showAuthModal, setShowAuthModal] = useState(false);

  useSEO({
    title: restaurant ? `${restaurant.name} | Roseville Restaurant` : 'Restaurant | Roseville',
    description: restaurant?.description || 'Discover great restaurants in Roseville, CA.',
    keywords: restaurant
      ? `${restaurant.name}, ${restaurant.cuisine}, Roseville restaurant, ${restaurant.category}, ${restaurant.address}, dining, food`
      : 'Roseville restaurants, food, dining',
    canonical: `/food-drink/${id}`,
    ogType: 'restaurant',
    ogImage: restaurant?.image,
    schema: restaurant
      ? {
          '@context': 'https://schema.org',
          '@graph': [
            generateRestaurantSchema(
              restaurant.name,
              restaurant.description,
              restaurant.address,
              '(916) 555-0100',
              `/food-drink/${id}`,
              restaurant.priceRange,
              restaurant.cuisine,
              restaurant.rating,
              250,
              menu,
              restaurant.hours
            ),
            generateBreadcrumbSchema([
              { name: 'Home', url: '/' },
              { name: 'Food & Drink', url: '/food-drink' },
              { name: restaurant.name, url: `/food-drink/${id}` }
            ])
          ]
        }
      : undefined
  });

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Restaurant Not Found</h1>
          <Link to="/food-drink" className="text-teal-600 hover:underline cursor-pointer">
            Back to Restaurants
          </Link>
        </div>
      </div>
    );
  }

  const otherRestaurants = restaurants.filter(r => r.id !== restaurantId).slice(0, 3);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center space-x-2 cursor-pointer">
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                <i className="ri-community-line text-white text-xl"></i>
              </div>
              <span className="text-xl font-bold text-gray-900">Roseville.org</span>
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <Link to="/" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">Home</Link>
              <Link to="/recreation" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">Recreation</Link>
              <Link to="/real-estate" className="text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">Real Estate</Link>
              <Link to="/food-drink" className="text-teal-600 font-semibold cursor-pointer">Food & Drink</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-6 py-3">
          <div className="flex items-center space-x-2 text-sm">
            <Link to="/" className="text-gray-500 hover:text-teal-600 cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <Link to="/food-drink" className="text-gray-500 hover:text-teal-600 cursor-pointer">Food & Drink</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-900 font-medium">{restaurant.name}</span>
          </div>
        </div>
      </div>

      {/* Hero */}
      <section className="relative h-96">
        <img
          src={restaurant.image}
          alt={restaurant.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
        
        {/* Favorite Button */}
        <div className="absolute right-6 top-6">
          <FavoriteButton
            itemType="restaurant"
            itemId={restaurant.id}
            itemTitle={restaurant.name}
            itemImage={restaurant.image}
            itemData={restaurant}
            onAuthRequired={() => setShowAuthModal(true)}
          />
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center gap-3 mb-3">
              <span className="px-3 py-1 bg-teal-500 text-white text-sm font-semibold rounded-full">
                {restaurant.cuisine}
              </span>
              <div className="px-3 py-1 bg-white/90 rounded-full flex items-center gap-1">
                <i className="ri-star-fill text-yellow-500"></i>
                <span className="font-semibold">{restaurant.rating}</span>
              </div>
              <span className="text-white font-semibold text-lg">{restaurant.priceRange}</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-2">{restaurant.name}</h1>
            <p className="text-white/90 text-lg">{restaurant.category}</p>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-3 gap-10">
            {/* Main Content */}
            <div className="lg:col-span-2">
              {/* About */}
              <div className="bg-white rounded-2xl p-8 shadow-sm mb-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">About</h2>
                <p className="text-gray-700 text-lg leading-relaxed">
                  {restaurant.description}
                </p>
              </div>

              {/* Menu */}
              {menu && (
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center space-x-2">
                    <i className="ri-restaurant-line text-teal-600"></i>
                    <span>Sample Menu</span>
                  </h2>
                  <div className="space-y-8">
                    {menu.map((section, idx) => (
                      <div key={idx}>
                        <h3 className="text-lg font-bold text-teal-600 mb-4 pb-2 border-b border-gray-200">
                          {section.category}
                        </h3>
                        <div className="space-y-4">
                          {section.items.map((item, itemIdx) => (
                            <div key={itemIdx} className="flex justify-between items-start">
                              <div className="flex-1 pr-4">
                                <h4 className="font-semibold text-gray-900">{item.name}</h4>
                                <p className="text-sm text-gray-600">{item.description}</p>
                              </div>
                              <span className="font-semibold text-teal-600 whitespace-nowrap">{item.price}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-sm text-gray-500 mt-6 pt-4 border-t border-gray-200">
                    * Menu items and prices subject to change. Please contact the restaurant for current offerings.
                  </p>
                </div>
              )}

              {!menu && (
                <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
                  <i className="ri-restaurant-line text-6xl text-gray-300 mb-4"></i>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Menu Coming Soon</h3>
                  <p className="text-gray-600">Contact the restaurant directly for menu information.</p>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-sm p-8 sticky top-24">
                <h3 className="text-xl font-bold text-gray-900 mb-6">Restaurant Info</h3>
                
                <div className="space-y-5">
                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                      <i className="ri-map-pin-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Address</p>
                      <p className="font-semibold text-gray-900">{restaurant.address}</p>
                      <p className="text-gray-700">Roseville, CA</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                      <i className="ri-time-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Hours</p>
                      <p className="font-semibold text-gray-900">{restaurant.hours}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                      <i className="ri-money-dollar-circle-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Price Range</p>
                      <p className="font-semibold text-gray-900">{restaurant.priceRange}</p>
                    </div>
                  </div>

                  <div className="flex items-start space-x-4">
                    <div className="w-10 h-10 flex items-center justify-center bg-teal-100 rounded-lg">
                      <i className="ri-restaurant-2-line text-teal-600 text-xl"></i>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Cuisine</p>
                      <p className="font-semibold text-gray-900">{restaurant.cuisine}</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-gray-200 space-y-3">
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(restaurant.name + ' ' + restaurant.address + ' Roseville CA')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full bg-teal-600 text-white text-center py-3 rounded-lg font-semibold hover:bg-teal-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-navigation-line mr-2"></i>
                    Get Directions
                  </a>
                  <a
                    href={`tel:916-555-0100`}
                    className="block w-full bg-gray-100 text-gray-900 text-center py-3 rounded-lg font-semibold hover:bg-gray-200 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-phone-line mr-2"></i>
                    Call Restaurant
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Other Restaurants */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8">More Restaurants</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {otherRestaurants.map((other) => (
              <Link
                key={other.id}
                to={`/food-drink/${other.id}`}
                className="group bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all cursor-pointer"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={other.image}
                    alt={other.name}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                  />
                  <div className="absolute top-3 right-3 px-2 py-1 bg-white/90 rounded-full flex items-center gap-1">
                    <i className="ri-star-fill text-yellow-500 text-sm"></i>
                    <span className="text-sm font-semibold">{other.rating}</span>
                  </div>
                </div>
                <div className="p-5">
                  <span className="text-xs text-teal-600 font-semibold">{other.cuisine}</span>
                  <h3 className="text-lg font-bold text-gray-900 mt-1 group-hover:text-teal-600 transition-colors">
                    {other.name}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">{other.address}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-teal-50 py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-10 h-10 bg-teal-600 rounded-lg flex items-center justify-center">
                <i className="ri-community-line text-white text-xl"></i>
              </div>
              <span className="text-xl font-bold text-gray-900">Roseville.org</span>
            </div>
            <p className="text-gray-600 text-sm">
              © 2025 Roseville.org. All rights reserved. | <a href="https://readdy.ai/?ref=logo" className="text-teal-600 hover:underline cursor-pointer">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>
      <AuthModal isOpen={showAuthModal} onClose={() => setShowAuthModal(false)} />
    </div>
  );
}
